import { useEffect, useState } from "react"
import { css, cx } from "@emotion/css"
import { Dimension } from "models/ratecard"
import { useI18n } from "providers/I18n"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import { InputAdornment } from "@material-ui/core"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { MorePopover } from "components/MorePopover"
import { HourRangeButtonTrigger } from "components/HourPicker/HourRangeButtonTrigger"
import { BaseRecord } from "components/Cable"
import { HourInterval } from "models/packages"
import { FilterOption, FilterSelect } from "./FilterSelect"
import { useSelection } from "hooks/useSelection"
import { NON_FILTERABLE } from "./utils"
import { useBaseRateFilters } from "./useBaseRateFilters"

type Props<T> = {
  categories?: Dimension[]
  data?: T[]
  timebelt?: HourInterval
  selectedFilters?: Record<string, { label: string; value: string }[]>
  onFiltersSelect: (name: string, selectedOptions: Array<any>) => void
  onTimebeltSelect: (hourInterval: HourInterval) => void
}

export function BaseRateFilters<T extends BaseRecord>(props: Props<T>) {
  const { t } = useI18n()

  const {
    categories,
    data = [],
    timebelt,
    selectedFilters,
    onFiltersSelect,
    onTimebeltSelect,
  } = props

  const [openMulti, setOpenMulti] = useState<Record<string, boolean>>(
    () => ({}),
  )
  const [, setFiltersState] = useState<Record<string, any>>({})

  const { selected: displayedFilters, onSelect: onFilterDisplay } =
    useSelection({
      onRemove: (filterId) => {
        setFiltersState(({ [filterId]: _, ...rest }) => ({ ...rest }))
      },
    })

  const availableFilters = categories
    ?.filter(
      (c) => !NON_FILTERABLE?.includes(c?.mediaFieldSemanticCode) && c.enabled,
    )
    ?.map((c) => c?.mediaFieldSemanticCode)

  const fieldFilters = displayedFilters?.filter((f) => f !== "timebelt")
  const { baseRateFilters } = useBaseRateFilters(data, fieldFilters)

  const onToggleOptions = (name: string) => (selectedOptions: Array<any>) =>
    onFiltersSelect(name, selectedOptions)

  const onSelectOpen = (option: string) => () =>
    setOpenMulti((o) => ({ ...o, [option]: true }))

  const onSelectClose = (option: string) => () =>
    setOpenMulti((o) => ({ ...o, [option]: false }))

  const handleFilterDisplay = (id: string) => {
    onFilterDisplay(id)
    id === "timebelt" ? onTimebeltSelect({}) : onFiltersSelect(id, [])
  }

  const options = availableFilters?.map((f) => ({
    id: f,
    labelKey: f,
  })) as FilterOption[]

  useEffect(() => {
    setFiltersState({})
  }, [data])

  return (
    <div className={styles.container}>
      {baseRateFilters?.map((c, cidx) => (
        <MultiSelect
          key={cidx}
          label={t(c?.name)}
          placeholder={t(c?.name)}
          items={c?.options || []}
          getOptionLabel={(option) => option?.label || ""}
          selectedValues={selectedFilters?.[c.name] || []}
          inputValue={
            selectedFilters?.[c?.name]
              ?.slice(0, 1)
              ?.map((s) => s?.label)
              ?.join(",") || ""
          }
          checkValues={() => true}
          renderOption={(option) => option?.label}
          onToggleOption={onToggleOptions(c?.name)}
          onOpen={onSelectOpen(c?.name)}
          open={!!openMulti[c?.name]}
          onClose={onSelectClose(c?.name)}
          margin="dense"
          className={styles.input}
          endAdornment={
            <>
              <MorePopover
                data={selectedFilters?.[c.name]}
                label={c?.name}
                name="label"
                className={styles.popover}
              />
              <DropDownAdornment open={!!openMulti[c?.name]} />
            </>
          }
        />
      ))}
      {displayedFilters?.some((f) => f === "timebelt") ? (
        <HourRangeButtonTrigger
          start={timebelt?.start ?? ""}
          end={timebelt?.end ?? ""}
          onRangeChange={onTimebeltSelect}
          label={t("timebelt.filter")}
          clearLabel={t("actions.clear")}
          submitLabel={t("actions.apply")}
          pickerSize="small"
          hasStartAdornment
          asTextButton
        />
      ) : null}
      <FilterSelect
        options={options}
        selected={displayedFilters}
        onSelect={handleFilterDisplay}
      />
    </div>
  )
}

const DropDownAdornment = ({ open }: { open: boolean }) => {
  return (
    <InputAdornment
      position="end"
      className={cx({ [styles.adornment]: !!open })}
    >
      {<DropdownIcon className={styles.dropdown} />}
    </InputAdornment>
  )
}

const styles = {
  container: css`
    display: flex;
    gap: var(--spacing-s);
    align-items: center;
    margin-bottom: var(--spacing-xl);
  `,
  input: css`
    width: 190px;
  `,
  dropdown: css`
    color: var(--dark-grey);
  `,
  adornment: css`
    transform: rotate(180deg);
  `,
  popover: css`
    margin: 0;
  `,
}
